// Cloud sync: mirrors the persisted snapshot to Firestore at states/{uid} while
// signed in. Load-on-login, debounced write-on-change, and live updates from
// other devices. Signed out, the app just uses localStorage (see state.ts).

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { user } from './auth';
import { writable } from 'svelte/store';
export const cloudLoading = writable(false);
import { snapshot, migrateSaved, subscribePersisted, applySignIn, applySignOut } from './state';
import { EXAMPLE_DATA_VERSION, loadExampleData } from './example';
import { loadCachedSuggestions, clearSuggestions } from './suggestions';
import { clearIsochroneCache } from './maps/overlays';

// True while we're applying remote data to the stores, so the change-listener
// doesn't immediately echo it straight back to the cloud.
let applyingRemote = false;
let unsubWrites: (() => void) | null = null;
let unsubDoc: (() => void) | null = null;
let writeTimer: ReturnType<typeof setTimeout> | null = null;
let currentUid: string | null = null;

function apply(data: unknown): boolean {
  applyingRemote = true;
  let upgradedExample = false;
  try {
    const saved = data as Parameters<typeof migrateSaved>[0];
    migrateSaved(saved);
    // Cloud-restored walkthroughs need the same bundled-data migrations as
    // locally restored ones, including the current center/zoom. Without this,
    // an older Firestore snapshot can make the example look zoomed out again.
    if (saved?.exampleLoaded && saved.exampleVersion !== EXAMPLE_DATA_VERSION) {
      loadExampleData();
      upgradedExample = true;
    }
    // Inputs may have changed underneath us — restore matching cached picks or
    // drop stale ones (migrateSaved bypasses clearSuggestions).
    loadCachedSuggestions();
  } finally {
    applyingRemote = false;
  }
  return upgradedExample;
}

function stopSync(): void {
  unsubWrites?.();
  unsubDoc?.();
  unsubWrites = null;
  unsubDoc = null;
  if (writeTimer) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
}

async function startSync(uid: string): Promise<void> {
  const ref = doc(db, 'states', uid);

  const first = await getDoc(ref);
  if (first.exists()) {
    const upgradedExample = apply(first.data()); // cloud is source of truth for a returning user
    if (upgradedExample) await setDoc(ref, snapshot());
  } else {
    await setDoc(ref, snapshot()); // first sign-in: seed cloud with local data
  }

  // Push local changes up (debounced); skip echoes of remote applies.
  unsubWrites = subscribePersisted(() => {
    if (applyingRemote) return;
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(() => {
      setDoc(ref, snapshot()).catch((e) => console.warn('Cloud save failed:', e));
    }, 600);
  });

  // Live updates from OTHER devices/tabs (ignore our own in-flight writes).
  unsubDoc = onSnapshot(ref, (snap) => {
    if (snap.metadata.hasPendingWrites || !snap.exists()) return;
    if (apply(snap.data())) {
      setDoc(ref, snapshot()).catch((e) => console.warn('Cloud example upgrade failed:', e));
    }
  });
}

/** Wire cloud sync to auth state. Call once at startup. */
export function initCloudSync(): void {
  user.subscribe((u) => {
    const uid = u?.uid ?? null;
    if (uid === currentUid) return;
    const wasSignedIn = currentUid != null;
    currentUid = uid;
    stopSync();
    if (uid) {
      // Sign-in: load this user's local cache (if any), then cloud on top.
      cloudLoading.set(true);
      applySignIn(uid);
      loadCachedSuggestions();
      startSync(uid).catch((e) => console.warn('Cloud sync init failed:', e)).finally(() => {
        if (currentUid === uid) cloudLoading.set(false);
      });
    } else if (wasSignedIn) {
      // Sign-out: clear the signed-in data from view, disk, and derived caches
      // so the signed-out sandbox starts clean.
      cloudLoading.set(false);
      applySignOut();
      clearSuggestions();
      clearIsochroneCache();
    }
  });
}
